/**
  * @class mmodOfficeAdmin
  * @description
  * Server Administration module is a helper module that provides administrator
  * with insight information on mServer itself.
  * There are five tabs with following information:
  * - SYSTEM: general host machine information including computer, OS, CPU, memory
  * - DATABASE: displays tables in schema mosy
  * - DISKS: displays disk volumes on the server with size and usage
  * - NETWORK: displays available network interfaces
  * - ALL: displays all data above in JSON structure
  *
  * Module is implemented without workflow, as a pure module named mmodOfficeAdmin.
  * It uses two messages to gather information from msvrServer:
  * mmGetSysInfo for gathering info on system, disks and network
  * and mmServerTablesGet to get info on database.
  */

import mmodModule from 'mmodModule'

export default class mmodOfficeAdmin extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: { type: 'object', default: { type: 'full' }, description: 'Initial window setup; same structure as required by `mmWindowSet`' },
      server_tables: { type: 'array', items: { type: 'string' }, default: ['wf_users', 'wf_applications', 'wf_configurations', 'wf_enrollments', 'wf_zones'], description: 'List of tables for admin download' },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm(require('./mmodOfficeAdmin.json'))
    this.mmStart()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by Window Manager when top full-screen window on any screen changes. Sends directly to that window and to #WM queue.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id]
  * @param {integer} [params.screen]
  */
  mmWindowFocused(sender, params) {
    if(!('id' in params) || params.id==this.listener_id) {
      this.mmButtonClicked(sender, { button: 'refresh' })
    }
  } // mmWindowFocused

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  async mmButtonClicked(sender, params) {
    if(params.button=='server_tables') {
      try{
        const { params } = await this.request(':msvrServer', 'mmServerTablesGet', { tables: this.config.server_tables }, 'mmServerTables')
        this.createDownloadFromBase64('application/zip', 'tables_'+_.ymdhms()+'.zip', params.zip)
      }catch(e) {
        this.modalError('Error getting tables: '+String(e))
      }
    }else if(params.button=='refresh') {
      try{
        const { params } = await this.request(':msvrServer', 'mmGetSysInfo', {}, 'mmSysInfo')
        this.refreshSystem(params)
        this.refreshDB(params.db)
        this.refreshDrives(params.fs)
        this.refreshNetwork(params.networkInterfaces)
        this.refreshRaw(params)
      }catch(e) {
        this.modalError('Error getting system info: '+String(e))
      }
    }
  } // mmButtonClicked

  refreshSystem(info) {
    const domains = ['system', 'cpu', 'mem', 'osInfo']
    const tab = this.el.find('mele-group@tab_system')
    tab.deleteChildren()
    domains.forEach(domain=>{
      const items = info[domain]
      if(!items || typeof (items)!=='object') return
      const group = tab.appendChildren({
        tag: 'mele-group',
        class: 'hr',
        header: 'sysinfo.'+domain,
      })
      for(const item in items) {
        let label = 'sysinfo.'+domain+'.'+item
        label = tab.translate(label, true) ? label : item
        let value = items[item]
        if(domain=='mem' && value>=0) value = (value/1024/1024).toFixed(0) + ' MB'
        group.appendChildren({
          tag: 'mele-input',
          type: 'string',
          label,
          value,
          readonly: true,
        })
      }
    })
  }

  refreshDB(db) {
    db = db || {}
    const all_tables = db.tables || []
    const schemas = {}
    all_tables.forEach(t=>schemas[t.schema]=1)
    let html = ''
    Object.keys(schemas).sort((a, b)=>a=='mosy'?-1:b=='mosy'?1:a.localeCompare(b)).forEach(schema=>{
      html += `<h2>Schema: ${schema}</h2><table><tr><th>Table</th><th>Rows</th><th>Index size</th><th>Toast size</th><th>Data size</th><th>Total size</th></tr>`
      const tables = all_tables.filter(t=>t.schema==schema).sort((a, b)=>b.total-a.total || a.table.localeCompare(b.table))
      html += tables.map(t=>`<tr><td>${t.table}</td><td>${t.rows}</td><td>${t.index}</td><td>${t.toast}</td><td>${t.data}</td><td>${t.total}</td></tr>`).join('')
      html += '</table>'
    })
    this.el.find('@db_tables').innerHTML = html
  }

  refreshDrives(disks) {
    disks = disks || []
    disks.forEach(disk=>{
      disk.size = disk.size>0 ? (disk.size/1024/1024).toFixed(1) +' MB' : disk.size
      disk.used = disk.used>0 ? (disk.used/1024/1024).toFixed(1) +' MB' : disk.size
      disk.use = disk.use>=0 ? parseFloat(disk.use).toFixed(1) + ' %' : disk.use
    })
    disks = disks.map(disk=>({ key: disk.fs, data: disk }))
    this.el.find('@fs_drives').setItems(disks)
  }

  refreshNetwork(nets) {
    const rows = []
    for(const id in nets) {
      const net = nets[id]
      rows.push({
        key: id,
        data: net,
      })
    }
    this.el.find('@net_if').setItems(rows)
  }

  refreshRaw(info) {
    this.el.find('@raw').innerHTML = JSON.stringify(info, null, '  ')
  }
} // mmodOfficeAdmin
